import { v4 as uuid } from 'uuid';
import { MissionConfig } from '../../Config/Missions';
import { getRandom } from '../../Utils/Utils';
import * as fromActions from '../Actions/Actions';
import { ActionTypes } from '../Actions/Actions';
import { LastLevel } from '../../Config/Levels';
import { ItemIDs } from '../../Config/Items';

export interface IDailyMission {
    uid: string;
    ts: number;
    action: string;
    tier: number;
    targetId: string;
    targetNum: number;
    missionState: string;
    total: number;
}

export interface IStateMissions {
    missions: IDailyMission[];
    refreshTimer: number;
    completedCount: number;
    milestone: number;
    missionsShown: boolean;
    claimedRewards: boolean[];
}

export const defaultMissions: IStateMissions = {
    missions: [],
    refreshTimer: 0,
    completedCount: 0,
    milestone: 0,
    missionsShown: false,
    claimedRewards: [false, false, false], // false: not yet claimed; true: claimed
};
export const StateMissions = (
    state = defaultMissions,
    action: fromActions.Actions
): IStateMissions => {
    switch (action.type) {
        case ActionTypes.MISSION_COMPLETE:
            const continuing = [...state.missions];
            const completed = continuing.filter(m => m.uid === action.payload)[0];
            const completedIndex = continuing.indexOf(completed);
            const refreshing = updateMissionState(MissionState.COMPLETE, completed);
            continuing[completedIndex] = refreshing;

            return {
                ...state,
                missions: continuing,
            };
        case ActionTypes.UPDATE_MISSION_COUNT:
            return {
                ...state,
                completedCount: state.completedCount + 1,
            };
        case ActionTypes.MISSION_INIT:
            return {
                ...state,
                missions: initMissions(action.payload[0], action.payload[1], action.payload[2]),
                refreshTimer: action.payload[2] ? 0 : state.refreshTimer,
            };
        case ActionTypes.MISSION_MILESTONE_REACHED:
            const m = action.payload < 3 ? action.payload : 0;
            const complete = m === 0 ? 0 : state.completedCount;
            return {
                ...state,
                milestone: m,
                completedCount: complete,
            };
        case ActionTypes.MISSION_REPLACE:
            const oldMissions = [...state.missions];
            const allMissions = [...state.missions];
            const remove = allMissions.filter(a => a.uid === action.payload[0])[0];
            const removeIndex = allMissions.indexOf(remove);
            allMissions[removeIndex] = null;
            const newMission = generateMission(
                Number(action.payload[1]),
                allMissions,
                oldMissions[removeIndex]
            );
            allMissions[removeIndex] = newMission;

            return {
                ...state,
                missions: allMissions,
            };
        case ActionTypes.MISSION_REFRESH:
            const continuing2 = [...state.missions];
            const completed2 = continuing2.filter(m => m.uid === action.payload)[0];
            const completedIndex2 = continuing2.indexOf(completed2);
            const refreshing2 = updateMissionState(MissionState.REFRESHING, completed2);
            continuing2[completedIndex2] = refreshing2;

            const freshTime = state.refreshTimer !== 0 ? state.refreshTimer : Date.now();
            return {
                ...state,
                missions: continuing2,
                refreshTimer: freshTime,
            };
        case ActionTypes.MISSION_REFRESH_COMPLETE:
            // replace all that are currently refreshing...
            const newMissionGroup = refreshMissions(state.missions, action.payload);

            return {
                ...state,
                missions: newMissionGroup,
                refreshTimer: 0,
            };
        case ActionTypes.MISSION_COUNT:
            const updateMissions = [...state.missions];
            const updateIndex = updateMissions.findIndex(m => m.uid === action.payload[0]);
            const updatedMission = countMissionProgress(
                updateMissions[updateIndex],
                action.payload[1]
            );
            updateMissions[updateIndex] = updatedMission;
            return {
                ...state,
                missions: updateMissions,
            };
        case ActionTypes.MISSIONS_SHOWN:
            return {
                ...state,
                missionsShown: true,
            };
        // refresh mission?..prepare next
        case ActionTypes.CHANGE_VIEW:
            return {
                ...state,
            };
        case ActionTypes.SET_MISSION_REWARD_CLAIMED:
            return {
                ...state,
                claimedRewards: action.payload,
            };

        default:
            return state;
    }
};

function initMissions(missions: IDailyMission[], playerIndex: number, refresh: boolean = false) {
    // let freshMissions = [...missions];
    while (missions.length < 5) {
        missions.push(generateMission(playerIndex, missions));
    }

    if (refresh) {
        missions = refreshMissions(missions, playerIndex);
    }

    return missions;
}

export function refreshMissions(missions: IDailyMission[], playerIndex: number) {
    const oldMissions = [...missions];
    // need to replace refreshing mission with new ones at same array index (for display purposes)
    const refreshPlaces = missions.reduce((arr, mission, index) => {
        if (!mission || mission.missionState === MissionState.REFRESHING) {
            missions[index] = null;
            arr.push(index);
        }
        return arr;
    }, []);

    return missions.reduce((arr, mission, index) => {
        if (refreshPlaces.includes(index)) {
            const n = generateMission(playerIndex, arr, oldMissions[index]);
            arr.push(n);
        } else {
            arr.push(mission);
        }
        return arr;
    }, []);
}

export function generateMission(
    levelIndex: number,
    curMissions: IDailyMission[],
    replacing?: IDailyMission
): IDailyMission {
    const playerOnLastLevel = levelIndex === LastLevel;
    const tier = chooseMissionTier(curMissions);
    const missionType = chooseMissionType(levelIndex, tier, curMissions, playerOnLastLevel);
    const targetType = chooseTargetType(levelIndex, missionType, playerOnLastLevel);
    const targetNum = chooseTargetNum(levelIndex, missionType, tier, targetType);

    if (replacing && !playerOnLastLevel) {
        if (
            missionType === replacing.action ||
            (missionType === replacing.action &&
                targetType === replacing.targetId &&
                targetNum === replacing.targetNum)
        ) {
            return generateMission(levelIndex, curMissions, replacing);
        }
    }

    return {
        uid: uuid(),
        ts: Date.now(),
        tier,
        action: missionType,
        targetId: targetType,
        targetNum: targetNum,
        missionState: MissionState.ACTIVE,
        total: 0,
    };
}

function chooseMissionType(
    playerLevelIndex: number,
    targetTier: number,
    curMissions: IDailyMission[],
    playerOnLastLevel: boolean
): string {
    curMissions = curMissions.filter(Boolean);
    const isLevelMission = (item: MissionTypes) =>
        item === MissionTypes.BEAT ||
        item === MissionTypes.COMPLETE ||
        item === MissionTypes.COLLECT;

    const available = Object.values(MissionTypes)
        .filter(m => !Number(m) && !(isLevelMission(m) && playerOnLastLevel))
        .reduce((arr, item) => {
            const num = curMissions.filter(m => m.action === item).length;
            if (num <= 0) {
                arr.push(item);
            }

            return arr;
        }, []);

    return available.length === 0
        ? MissionTypes.COLLECT
        : available.length === 1
            ? available[0]
            : available[getRandom(0, available.length - 1)];
}

function chooseMissionTier(curMissions: IDailyMission[]) {
    curMissions = curMissions.filter(Boolean);
    // const tier1 = curMissions.filter((m) => m.tier === 1).length;
    const tier2 = curMissions.filter(m => m.tier === 2).length;
    const tier3 = curMissions.filter(m => m.tier === 3).length;

    if (tier3 === 0) {
        return 3;
    }

    if (tier2 < 2) {
        return 2;
    }

    return 1;
}

function chooseTargetNum(
    playerLevelIndex: number,
    missionAction: string,
    tier: number,
    targetType: string
): number {
    const a = missionAction;
    if (a === MissionTypes.COMPLETE) {
        const completeRange = MissionConfig.completeLevels[tier - 1];
        return Math.min(
            LastLevel - playerLevelIndex + 1,
            getRandom(completeRange[0], completeRange[completeRange.length - 1])
        );
    }

    if (a === MissionTypes.BEAT) {
        let targetLevel = 0;
        if (tier === 1) {
            targetLevel = playerLevelIndex + 1;
        }

        if (tier === 2) {
            targetLevel = playerLevelIndex + 2;
        }

        targetLevel = playerLevelIndex + 3;
        return Math.min(LastLevel, targetLevel);
    }

    if (a === MissionTypes.REACH) {
        return MissionConfig.reachCombo[tier - 1][0];
    }

    if (a === MissionTypes.COLLECT) {
        const config =
            targetType === 'stars' ? MissionConfig.collectStars : MissionConfig.collectToys;
        const range = config[tier - 1];
        const random = getRandom(range[0], range[range.length - 1]);
        const target =
            targetType === 'stars' ? Math.min(random, (LastLevel - playerLevelIndex) * 3) : random;
        return target;
    }

    if (a === MissionTypes.USE) {
        const useId = 'use_' + targetType.toLowerCase();
        const useRange = MissionConfig[useId][tier - 1];
        return getRandom(useRange[0], useRange[useRange.length - 1]);
    }

    if (a === MissionTypes.MATCH) {
        const matchRange = MissionConfig.matchTiles[tier - 1];
        let mr = getRandom(matchRange[0], matchRange[matchRange.length - 1]);
        if (mr % 2 !== 0) {
            if (mr - 1 < matchRange[0]) {
                mr += 1;
            } else {
                mr -= 1;
            }
        }
        return mr;
    }

    if (a === MissionTypes.MATCHGOLD) {
        const goldRange = MissionConfig.matchGold[tier - 1];
        let mg = getRandom(goldRange[0], goldRange[goldRange.length - 1]);
        if (mg % 2 !== 0) {
            if (mg - 1 < goldRange[0]) {
                mg += 1;
            } else {
                mg -= 1;
            }
        }
        return mg;
    }

    return 0;
}

function chooseTargetType(
    playerLevelIndex: number,
    missionAction: string,
    playerOnLastLevel: boolean
) {
    // if beat or complete....always level
    const a = missionAction;
    if (a === MissionTypes.BEAT || a === MissionTypes.COMPLETE) {
        return 'level';
    }
    // reach...always combo meter
    if (a === MissionTypes.REACH) {
        return 'combo'; // will need to store previous of this type (or number of) so target can get higher
    }
    // collect...stars or toys, etc
    if (a === MissionTypes.COLLECT) {
        const collectTypes: string[] = ['toys'];
        if (!playerOnLastLevel) {
            collectTypes.push('stars');
        }
        return collectTypes[getRandom(0, collectTypes.length - 1)];
    }
    // if use...get booster type, will need to be one they can use. also don't choose this type for players in early levels
    if (a === MissionTypes.USE) {
        return chooseMissionBooster(playerLevelIndex);
    }

    if (a === MissionTypes.MATCH) {
        return 'pair';
    }

    if (a === MissionTypes.MATCHGOLD) {
        return 'gold';
    }

    return 'none';
}

function chooseMissionBooster(playerIndex: number): string {
    const available = [];
    if (playerIndex >= 5) {
        available.push(ItemIDs.FIRECRACKER);
    }

    if (playerIndex >= 20) {
        available.push(ItemIDs.MAGNET);
    }

    return available[getRandom(0, available.length - 1)];
}

function countMissionProgress(mission: IDailyMission, n: number) {
    const total = mission.total + n;
    return {
        ...mission,
        total,
    };
}

function updateMissionState(newState: string, mission: IDailyMission): IDailyMission {
    return {
        ...mission,
        missionState: newState,
    };
}

export enum MissionTypes {
    COLLECT = 'collect',
    COMPLETE = 'complete',
    USE = 'use',
    REACH = 'reach',
    BEAT = 'beat',
    MATCH = 'match',
    MATCHGOLD = 'matchgold',
}

export enum MissionState {
    ACTIVE = 'active',
    COMPLETE = 'complete',
    REFRESHING = 'refreshing',
}
